import { storeToRefs } from 'pinia'
import { useCommonStore } from '@/stores/common.ts'
import { useEditorStore } from '@/stores/editor.ts'
import { nextTick } from 'vue'
import { useChart } from '@/composables/chart/useChart.ts'
import { addFileBlock, downloadFile, deleteFile, allDeleteFile } from '@/views/editor/composable/useFile.ts'
import { useCreateTable } from '@/views/editor/composable/table/useCreateTable.ts'
import { UseSelection } from '@/views/editor/composable/useSelection.ts'
import { deletePrefix, addPrefix, setDataType, handlerClose } from './useNoteUtils'
import { newBlockEvent } from './useBlockEvent'
import { useImageCaption } from '@/views/editor/composable/image/useImageCaption.ts'
import { sanitizeCellContent, stripFunctionCloseButton } from '@/composables/common.ts'
import { restoreLatexElements } from '@/views/editor/composable/useLatexRestore.ts'

const rebindImageCaption = (blockElement: HTMLElement) => {
	useImageCaption().rebindCaptionEvents(blockElement)
}

/**
 * 특정 타입의 새 블록 생성 이벤트 처리
 * @param type 블록 타입 (text, chart, file 등)
 * @param content 초기 내용
 */
export const createBlockEvent = async (type: string = 'text', content: string = '') => {
	await nextTick()
	const { socket } = storeToRefs(useCommonStore())
	const { selector, noteUid, changeRange, oldBlockUid, noteType, chartDataList, chartData, enterSelector, isWrite } = storeToRefs(useEditorStore())
	oldBlockUid.value = `temp-${useCommonStore().uniqueId()}`
	changeRange.value = null

	let target = selector.value ? selector.value : enterSelector.value
	const editorBlockList = document.querySelectorAll('.editor--block')

	let nowBlockUid = ''

	// 사이드 메뉴에서 생성 시 타겟 설정
	if (type === 'side') {
		target = enterSelector.value
		handlerClose()

		// active 제거
		editorBlockList.forEach((item: any) => {
			item.classList.remove('active')
		})
	}

	if (target) {
		nowBlockUid = target.id
	}

	// DOM 삽입용 ID 보존 (temp ID도 DOM에서는 유효하므로 원본 유지)
	const domInsertBlockUid = nowBlockUid

	// 소켓 전송용: temp-로 시작하면 서버가 인식할 수 없으므로 마지막 유효 블록으로 교체
	if (nowBlockUid.startsWith('temp-')) {
		const validBlocks = [...editorBlockList].filter((b: any) => !b.id.startsWith('temp-'))
		const lastValid: any = validBlocks[validBlocks.length - 1]
		if (lastValid) {
			nowBlockUid = lastValid.id
		} else {
			nowBlockUid = ''
		}
	}

	// 텍스트 타입이고 현재 블록 ID가 없는 경우 마지막 블록을 타겟으로 설정
	if (type === 'text' && !nowBlockUid) {
		editorBlockList.forEach((item: any, index: number) => {
			if (index === editorBlockList.length - 1) {
				nowBlockUid = item.id
			}
		})
	} else if (!nowBlockUid) {
		// 그 외의 경우 타겟 ID와 일치하는 블록 찾기
		editorBlockList.forEach((item: any) => {
			if (deletePrefix(item.id) === deletePrefix(target.id)) {
				nowBlockUid = item.id
			}
			item.classList.remove('active')
		})
	}

	const containerDiv = document.createElement('div')
	containerDiv.classList.add('editor--block__container')
	containerDiv.contentEditable = 'false'
	const newDiv = document.createElement('div')

	// 차트 타입 블록 설정
	if (type === 'chart') {
		newDiv.id = oldBlockUid.value
		newDiv.classList.add('editor--block')
		newDiv.classList.add('active')
		newDiv.dataset.type = 'chart'
		newDiv.setAttribute('contenteditable', 'false')
	} else {
		// 텍스트 타입 블록 설정
		newDiv.id = oldBlockUid.value
		newDiv.classList.add('editor--block')
		newDiv.dataset.type = 'text'
		newDiv.setAttribute('contenteditable', 'true')
		newDiv.setAttribute('tabIndex', '1')
		newDiv.innerHTML = '\u200B'
	}

	containerDiv.appendChild(newDiv)

	const nowBlock: any = document.getElementById(domInsertBlockUid || nowBlockUid)

	// 현재 블록 뒤에 새 블록 삽입
	if (nowBlock) {
		nowBlock.closest('.editor--block__container').insertAdjacentElement('afterend', containerDiv)

		if (type === 'chart') {
			chartDataList.value.chartTargetUid = deletePrefix(newDiv.id)
			const data = {
				blockUid: deletePrefix(newDiv.id),
				chartDataList: chartDataList.value,
			}

			chartData.value.push(data)
		}
		newDiv.classList.add('active')
		selector.value = newDiv
		await nextTick()
		// 포커스 및 커서 이동
		newDiv.focus({ preventScroll: true })
		const us = new UseSelection()
		us.setElement(newDiv)
		us.moveCursorToStart()
		await nextTick() // DOM 업데이트 보장
	} else {
		// 블록이 없는 경우 컨테이너에 추가
		const container: any = document.querySelector('.editor--container__content')
		container.appendChild(containerDiv)

		// 참조 블록이 DOM에 없으므로 prevUID를 null로 초기화
		nowBlockUid = ''

		newDiv.classList.add('active')
		selector.value = newDiv
		await nextTick()
		newDiv.focus({ preventScroll: true })
		const us = new UseSelection()
		us.setElement(newDiv)
		us.moveCursorToStart()
		await nextTick()
	}

	await nextTick()

	// 프로젝트 타입인 경우 소켓으로 블록 생성 요청
	if (noteType.value === 'Project') {
		const { isLoadNote } = storeToRefs(useEditorStore())

		// isLoadNote가 true면 이전 블록 생성이 완료될 때까지 대기 (최대 3초)
		if (isLoadNote.value) {
			await new Promise<void>(resolve => {
				const startTime = Date.now()
				const checkInterval = setInterval(() => {
					if (!isLoadNote.value || Date.now() - startTime > 3000) {
						clearInterval(checkInterval)
						resolve()
					}
				}, 50)
			})
		}

		if (type === 'chart') {
			socket.value.createNoteBlock(noteUid.value, deletePrefix(nowBlockUid), oldBlockUid.value, JSON.stringify(chartDataList.value))
		} else if (type === 'file') {
			socket.value.createNoteBlock(noteUid.value, deletePrefix(nowBlockUid), oldBlockUid.value, content)
		} else {
			socket.value.createNoteBlock(noteUid.value, deletePrefix(nowBlockUid), oldBlockUid.value, newDiv.innerHTML)
		}
	} else if (noteType.value === 'Template' && type === 'file') {
		const { newBlockId } = storeToRefs(useEditorStore())
		await nextTick()
		newBlockId.value = oldBlockUid.value
	}

	if (type === 'chart') {
		useChart().init()
	}
	newBlockEvent()
}

let isCreatingBlock = false

/**
 * 블록의 마지막 유효한 노드를 찾는 헬퍼 함수
 * (중첩된 .editor--block 제외)
 */
const getLastValidNode = (blockElement: HTMLElement): Node | null => {
	const childNodes = Array.from(blockElement.childNodes)

	// 뒤에서부터 순회하며 블록이 아닌 마지막 노드 찾기
	for (let i = childNodes.length - 1; i >= 0; i--) {
		const node = childNodes[i]

		// 요소 노드인 경우 .editor--block인지 확인
		if (node.nodeType === Node.ELEMENT_NODE) {
			const element = node as HTMLElement
			if (element.classList.contains('editor--block')) {
				continue // 중첩된 블록은 건너뛰기
			}
			return node
		}

		// 텍스트 노드인 경우 바로 반환
		if (node.nodeType === Node.TEXT_NODE) {
			return node
		}
	}

	return null
}

/**
 * 일반 텍스트 블록 생성 (Enter 키 입력 시 등)
 */
export const createBlock = async () => {
	if (isCreatingBlock) return
	isCreatingBlock = true

	try {
		await nextTick()
		const { changeRange } = storeToRefs(useEditorStore())
		changeRange.value = null
		const { socket } = storeToRefs(useCommonStore())
		const { selector, noteUid, oldBlockUid, noteType, isWrite } = storeToRefs(useEditorStore())

		if (!selector.value?.id) {
			const activeBlock = document.activeElement?.closest('.editor--block') as HTMLElement | null
			if (activeBlock) {
				selector.value = activeBlock
			} else {
				return
			}
		}

		const nowBlockUid = deletePrefix(selector.value.id)
		oldBlockUid.value = `temp-${useCommonStore().uniqueId()}`

		const currentContainer = selector.value.closest('.editor--block__container')
		if (!currentContainer) {
			return
		}

		// 현재 커서 위치에서 텍스트 분리
		const selection = window.getSelection()
		let afterContent = ''

		if (selection && selection.rangeCount > 0) {
			const range = selection.getRangeAt(0)

			// 커서 위치 이후의 내용 추출
			const afterRange = range.cloneRange()

			// 자식 블록이 있다면 제외하고, 현재 블록의 마지막 위치까지만 설정
			const lastValidNode = getLastValidNode(selector.value)
			if (lastValidNode) {
				afterRange.setEndAfter(lastValidNode)
			} else {
				afterRange.setEnd(selector.value, selector.value.childNodes.length)
			}

			const fragment = afterRange.extractContents()

			// 추출된 fragment에서 블록 요소 제거
			const tempDiv = document.createElement('div')
			tempDiv.appendChild(fragment)

			// 중첩된 블록 제거
			const nestedBlocks = tempDiv.querySelectorAll('.editor--block')
			nestedBlocks.forEach(block => block.remove())

			afterContent = tempDiv.innerHTML

			// 현재 블록이 비어있으면 ZWSP 추가
			if (selector.value.innerHTML.trim() === '') {
				selector.value.innerHTML = '\u200B'
			}
		}

		const newContainer = document.createElement('div')
		newContainer.classList.add('editor--block__container')
		newContainer.contentEditable = 'false'

		const newBlock = document.createElement('div')
		newBlock.id = oldBlockUid.value
		newBlock.classList.add('editor--block', 'active')
		newBlock.dataset.type = 'text'
		newBlock.setAttribute('contenteditable', 'true')
		newBlock.setAttribute('tabIndex', '1')

		// 분리된 내용을 새 블록에 넣기, 없으면 ZWSP
		newBlock.innerHTML = afterContent || '\u200B'

		newContainer.appendChild(newBlock)
		currentContainer.insertAdjacentElement('afterend', newContainer)

		const editorBlockList = document.querySelectorAll('.editor--block')
		editorBlockList.forEach((block: any) => {
			block.classList.remove('active')
		})
		newBlock.classList.add('active')
		selector.value = newBlock

		await nextTick()

		newBlockEvent()
		newBlock.focus({ preventScroll: true })
		const us = new UseSelection()
		us.setElement(newBlock)
		us.moveCursorToStart()
		await nextTick() // DOM 업데이트 보장

		// focus/focusout 이벤트 순서에 의해 active가 제거될 수 있으므로 명시적으로 보장
		if (newBlock.dataset.type === 'text') {
			document.querySelectorAll('.editor--block').forEach((b: any) => b.classList.remove('active'))
			newBlock.classList.add('active')
		}

		if (noteType.value === 'Project') {
			await nextTick()
			requestAnimationFrame(async () => {
				const documentBlock: any = document.getElementById(addPrefix(nowBlockUid))
				if (documentBlock) {
					await socket.value.modifyNoteBlock(noteUid.value, deletePrefix(nowBlockUid), documentBlock.innerHTML)
					socket.value.createNoteBlock(noteUid.value, nowBlockUid, oldBlockUid.value, newBlock.innerHTML)
					socket.value.blockEditEnd(noteUid.value, deletePrefix(nowBlockUid))
				}
			})
		} else if (noteType.value === 'Template') {
			const { blockList } = storeToRefs(useEditorStore())
			await nextTick()

			// DOM 순서 확인
			const allBlocks = document.querySelectorAll('.editor--block')
			let newBlockDOMIndex = -1
			allBlocks.forEach((block, index) => {
				if (block.id === oldBlockUid.value) {
					newBlockDOMIndex = index
				}
			})

			// blockList에 새 블럭 추가
			const newBlockData = {
				blockUid: deletePrefix(oldBlockUid.value),
				content: newBlock.innerHTML,
				noteUid: noteUid.value,
				projectUid: '',
				reviewCnt: 0,
				sortOrder: newBlockDOMIndex,
				status: 0,
				type: 'text',
			}

			// sortOrder 기반으로 삽입 위치 찾기
			let insertIndex = blockList.value.findIndex((block: any) => {
				const blockOrder = block.sortOrder !== undefined ? block.sortOrder : 0
				return blockOrder > newBlockData.sortOrder
			})

			if (insertIndex === -1) {
				blockList.value.push(newBlockData)
			} else {
				blockList.value.splice(insertIndex, 0, newBlockData)
			}
		}
	} finally {
		setTimeout(() => {
			isCreatingBlock = false
		}, 100)
	}
}

/**
 * 블록 리스트를 화면에 렌더링 (초기 로딩 또는 업데이트 시)
 */
export const addBlockList = () => {
	const { blockList, isWrite } = storeToRefs(useEditorStore())
	const blockWrap = document.querySelector('.editor--container__content')
	const blockContainerList = blockWrap?.querySelectorAll('.editor--block__container')
	blockContainerList?.forEach((blockContainer: any) => blockContainer.remove())
	blockList.value.forEach((block: any) => {
		const blockContainer = document.createElement('div')
		blockContainer.classList.add('editor--block__container')
		blockContainer.contentEditable = 'false'
		const blockElement = document.createElement('div')
		blockElement.classList.add('editor--block')
		const blockUid = block.blockUid || `temp-${useCommonStore().uniqueId()}`
		blockElement.id = addPrefix(blockUid)
		blockElement.dataset.type = block.type || 'text'
		blockElement.setAttribute('tabIndex', '1')
		switch (blockElement.dataset.type) {
			case 'text':
				blockElement.setAttribute('contenteditable', isWrite.value ? 'true' : 'false')
				blockElement.innerHTML = block.content || '\u200B'
				restoreLatexElements(blockElement)
				break

			case 'defaultTable':
				blockElement.setAttribute('contenteditable', 'false')
				try {
					const content = typeof block.content === 'string' ? JSON.parse(block.content) : block.content
					if (content) {
						const tableUid = content.tableUID || content.tableUid
						if (tableUid) {
							content.tableUID = tableUid
							content.tableUid = tableUid
						}
					}
					// useCreateTable 훅을 이용해 insertTable 호출
					blockElement.innerHTML = String(useCreateTable().insertTable(content, blockUid))
				} catch {
					blockElement.innerHTML = ''
				}
				break

			case 'image':
				blockElement.setAttribute('contenteditable', 'false')
				blockElement.innerHTML = block.content
				// 이미지 드래그 방지
				const imgElement = blockElement.querySelector('img')
				if (imgElement) {
					imgElement.setAttribute('draggable', 'false')
				}
				rebindImageCaption(blockElement)
				break

			case 'chart':
				blockElement.setAttribute('contenteditable', 'false')
				blockElement.innerHTML = ''
				break

			case 'file':
				blockElement.setAttribute('contenteditable', 'false')
				blockElement.appendChild(addFileBlock(block.content))

				const blockFileList = blockElement.querySelectorAll('.file--content__list > div')
				blockFileList.forEach((blockFile: any) => {
					blockFile.addEventListener('click', downloadFile)
				})

				const blockFileDelete = blockElement.querySelectorAll('.file--content__list > button')
				blockFileDelete.forEach((blockFile: any) => {
					blockFile.addEventListener('click', deleteFile)
				})

				const blockFileAllDelete: any = blockElement.querySelector('.file--title button')
				blockFileAllDelete.addEventListener('click', allDeleteFile)
				break
		}

		blockContainer.appendChild(blockElement)

		// 리뷰 카운트 표시
		if (block.reviewCnt > 0) {
			const newDiv = document.createElement('div')
			newDiv.classList.add('comment--container')
			newDiv.dataset.setid = blockUid
			newDiv.setAttribute('contenteditable', 'false')
			newDiv.innerHTML = `<span data-testid="editor-comment-count">${block.reviewCnt}</span> <i class="ico ico--comment" data-testid="editor-comment-icon"></i>`
			blockContainer.appendChild(newDiv)
		}

		blockWrap?.appendChild(blockContainer)
	})

	newBlockEvent()
	try {
		useCreateTable().event()
	} catch (error) {}
}

/**
 * 테이블 요소로부터 데이터 구조 생성
 * @param table HTMLTableElement
 * @returns 테이블 데이터 객체
 */
export const setTableData = (table: HTMLTableElement, preserveTempIds = false) => {
	const rowList: any = [...table.rows]
	const rows: any = []

	let allSize: any = {
		width: [],
		height: [],
	}

	const firstDataRow = rowList[1]
	if (firstDataRow) {
		const firstDataCell = firstDataRow.querySelector('td:not(.number--line)') as HTMLElement
		if (firstDataCell && firstDataCell.dataset.size) {
			try {
				const sizeData = JSON.parse(firstDataCell.dataset.size)
				if (sizeData.width && sizeData.height) {
					allSize = sizeData
				}
			} catch (e) {}
		}
	}

	if (allSize.width.length === 0) {
		for (let i = 1; i <= rowList[0].querySelectorAll('td.number--line').length - 1; i++) {
			const cell = rowList[0].querySelectorAll('td.number--line')[i] as HTMLElement
			let width = 120
			if (cell) {
				if (cell.style.width) {
					width = Math.round(parseFloat(cell.style.width))
				} else {
					width = Math.round(cell.getBoundingClientRect().width)
				}
			}
			allSize.width.push(width)
		}
	}

	if (allSize.height.length === 0) {
		rowList.forEach((row: any, rowIndex: number) => {
			if (rowIndex !== 0) {
				const cellList = [...row.cells]
				rows[rowIndex - 1] = []

				const numberLine = cellList[0] as HTMLElement
				if (numberLine && numberLine.classList.contains('number--line')) {
					const height = numberLine.getBoundingClientRect().height
					const adjustedHeight = Math.round(height) - 1
					allSize.height.push(adjustedHeight)
				} else {
					allSize.height.push(40)
				}
			}
		})
	}

	for (let i = 0; i < rowList.length; i++) {
		if (i === 0) continue
		rows[i - 1] = []
		const cellList: any = [...rowList[i].cells]
		for (let j = 0; j < cellList.length; j++) {
			if (j === 0) continue
			const dataType: any = setDataType(cellList[j], allSize)

			const colSpan = cellList[j].colSpan || 1
			const rowSpan = cellList[j].rowSpan || 1

			let cellId: string
			if (cellList[j].id) {
				cellId = cellList[j].id.replace('prefix-', '')
				if (!preserveTempIds && cellId.startsWith('temp-')) {
					cellId = useCommonStore().uniqueId()
				}
			} else {
				cellId = useCommonStore().uniqueId()
			}

			let cellContent: string
			if (cellList[j].dataset.type === 'function' || cellList[j].dataset.type === 'error') {
				cellContent = cellList[j].querySelector('span')?.innerHTML ?? cellList[j].querySelector('div')?.innerHTML ?? ''
			} else if (cellList[j].dataset.type === 'image') {
				cellContent = cellList[j].querySelector('img')?.outerHTML ?? ''
			} else {
				cellContent = cellList[j].querySelector('div')?.innerHTML ?? ''
			}

			rows[i - 1].push({
				cellUid: cellId,
				colSpan: colSpan,
				rowSpan: rowSpan,
				content: sanitizeCellContent(cellContent),
				dataType: dataType,
			})
		}
	}

	let tableUid = ''
	if (table.id) {
		tableUid = table.id.replace(/^prefix-/, '')
	}

	return {
		cell: rows,
		allSize: allSize,
		tableUid: tableUid,
	}
}

/**
 * 테이블 텍스트 데이터 추출
 * @param table HTMLTableElement
 * @returns 텍스트 데이터 객체
 */
export const setTableTextData = (table: HTMLTableElement) => {
	const rowList: any = [...table.rows]
	const rows: any = []

	for (let i = 0; i < rowList.length; i++) {
		rows[i] = []
		const cellList: any = [...rowList[i].cells]
		for (let j = 0; j < cellList.length; j++) {
			const dataType: any = {}

			Object.entries(cellList[j].dataset).forEach(([key, value]) => {
				if (key !== 'colSpan' && key !== 'rowSpan') {
					dataType[key] = value
				}
			})

			// number line(행/열 헤더)은 데이터에서 제외하고 계속 진행
			if (cellList[j].classList.contains('number--line')) continue

			const rawCellId = cellList[j].id.includes('prefix') ? deletePrefix(cellList[j].id) : null
			const resolvedCellUid = rawCellId && !rawCellId.startsWith('temp-') ? rawCellId : useCommonStore().uniqueId()
			rows[i].push({
				cellUid: resolvedCellUid,
				colSpan: cellList[j].colSpan || 1,
				rowSpan: cellList[j].rowSpan || 1,
				content: cellList[j].textContent,
				dataType: dataType,
			})
		}
	}

	return {
		cell: rows,
	}
}

/**
 * 데이터로부터 테이블 요소 생성 (현재 미사용 또는 불완전해 보임)
 * @param data 테이블 데이터
 */
export const setTableElement = (data: any) => {
	const table: HTMLTableElement = document.createElement('table')
	let tableRow: number = 0
	let tableCol: number = 0

	if (data.content.tableUid || data.content.tableUID) {
		table.id = `prefix-${data.content.tableUid || data.content.tableUID}`
	} else {
		table.id = `prefix-${useCommonStore().uniqueId()}`
	}
	table.setAttribute('tabindex', '0')
	table.setAttribute('data-testid', 'editor-table-container')

	let rowList: any

	if (data.content.rows && data.content.rows.length > 0) {
		rowList = data.content.rows
	} else {
		rowList = data.content.cells
	}

	const allSize = data.content.allSize || { width: [], height: [] }

	// rowspan/colspan을 추적하기 위한 맵 (행, 열 → 차지 여부)
	const occupiedMap: Map<string, boolean> = new Map()

	rowList.forEach((rows: any, rowIndex: number) => {
		tableRow++
		const newRow: HTMLTableRowElement = table.insertRow(rowIndex)
		if (rows.rowUid) {
			newRow.id = rows.rowUid
		}

		let logicalColIndex = 0 // 논리적 열 인덱스

		rows.forEach((cells: any, cellIndex: number) => {
			// 현재 논리적 열이 이전 행의 rowspan으로 차지되어 있으면 건너뛰기
			while (occupiedMap.get(`${rowIndex}-${logicalColIndex}`)) {
				logicalColIndex++
			}

			const newCell: HTMLTableCellElement = newRow.insertCell(-1) // 행 끝에 추가
			if (cells.cellUid) {
				newCell.id = `prefix-${cells.cellUid}`
			} else {
				// 초기 히스토리에 cellUid가 없는 경우 새 ID 생성
				const newId = useCommonStore().uniqueId()
				newCell.id = `prefix-${newId}`
				cells.cellUid = newId
			}
			newCell.setAttribute('data-testid', `editor-block-inner-table-cell-${rowIndex}${logicalColIndex}`)
			if (cells.dataType) {
				Object.keys(cells.dataType).forEach(key => {
					newCell.dataset[key] = cells.dataType[key]
				})
			}
			const colSpan = cells.colSpan && cells.colSpan > 1 ? cells.colSpan : 1
			const rowSpan = cells.rowSpan && cells.rowSpan > 1 ? cells.rowSpan : 1

			// 첫 번째 행일 때 tableCol 계산 (colspan 고려)
			if (rowIndex === 0) {
				tableCol += colSpan
			}

			if (colSpan > 1) {
				newCell.colSpan = colSpan
			}
			if (rowSpan > 1) {
				newCell.rowSpan = rowSpan
			}

			// 이 셀이 차지하는 모든 위치를 occupiedMap에 기록
			for (let r = 0; r < rowSpan; r++) {
				for (let c = 0; c < colSpan; c++) {
					occupiedMap.set(`${rowIndex + r}-${logicalColIndex + c}`, true)
				}
			}

			// colspan 고려하여 너비 합산
			if (colSpan > 1) {
				let totalWidth = 0
				for (let c = 0; c < colSpan; c++) {
					if (allSize.width[logicalColIndex + c]) {
						totalWidth += allSize.width[logicalColIndex + c]
					}
				}
				if (totalWidth > 0) {
					newCell.style.width = `${totalWidth}px`
				}
			} else if (allSize.width[logicalColIndex]) {
				newCell.style.width = `${allSize.width[logicalColIndex]}px`
			}
			const newDiv: HTMLDivElement = document.createElement('div')

			const isFunction = cells.dataType?.type === 'function'
			const isHeader = cells.dataType?.header === 'true'
			newDiv.setAttribute('contenteditable', isFunction || isHeader ? 'false' : 'true')

			// rowspan 고려하여 높이 합산
			if (rowSpan > 1) {
				let totalHeight = 0
				for (let r = 0; r < rowSpan; r++) {
					if (allSize.height[rowIndex + r]) {
						totalHeight += allSize.height[rowIndex + r]
					}
				}
				if (totalHeight > 0) {
					newDiv.style.height = `${totalHeight}px`
				}
			} else if (allSize.height[rowIndex]) {
				newDiv.style.height = `${allSize.height[rowIndex]}px`
			}
		if (cells.content) {
			if (isFunction) {
				newDiv.innerHTML = `<span>${stripFunctionCloseButton(cells.content)}</span><i class="ico ico--table__close" data-testid="table-close-btn"></i>`
			} else {
				newDiv.innerHTML = cells.content
			}
		}
			newCell.appendChild(newDiv)
			newRow.appendChild(newCell)

			// 다음 셀의 논리적 열 인덱스 증가
			logicalColIndex += colSpan
		})
	})

	const numberRow = table.insertRow(0)

	for (let i: number = 0; i <= tableCol; i++) {
		const newCell: HTMLTableCellElement = numberRow.insertCell(i)
		newCell.classList.add('number--line')
		newCell.setAttribute('data-testid', `editor-block-row-number-line-${i}`)
		if (i === 0) {
			newCell.style.width = '20px'
		} else {
			newCell.innerHTML = String.fromCharCode(i + 96).toUpperCase()
			if (allSize.width[i - 1]) {
				newCell.style.width = `${allSize.width[i - 1]}px`
			}
		}
	}

	for (let i: number = 0; i < tableRow; i++) {
		const newRow = [...table.rows][i + 1]
		const newCell: HTMLTableCellElement = newRow.insertCell(0)
		newCell.classList.add('number--line')
		newCell.setAttribute('data-testid', `editor-block-column-number-line-${i}`)
		newCell.innerHTML = String(i + 1)
		if (allSize.height[i]) {
			newCell.style.height = `${allSize.height[i] + 1}px`
		}
	}

	return table.outerHTML
}

export const lastBlockCheck = async () => {
	const { selector } = storeToRefs(useEditorStore())

	const editor = document.getElementById('editor')
	const block = [...document.querySelectorAll('.editor--block')]
	let blockElement: any = block[block.length - 1]

	if (!blockElement) {
		// 블록이 하나도 없으면 새 블록 생성
		await createBlockEvent('text')
		return
	}

	const isLockedByOther = blockElement.getAttribute('data-avatar-locked') === 'true'

	if (blockElement.dataset.type !== 'text' || isLockedByOther) {
		// text 타입이 아니거나, 다른 사용자가 편집 중이면 새 블록 생성
		selector.value = blockElement
		await createBlockEvent('text')
	} else {
		// text 타입이고 잠기지 않았으면 마지막 블록으로 포커스 이동
		selector.value = blockElement
		const selection = new UseSelection()
		selection.setElement(blockElement)

		selection.changeBlockFocus(blockElement)
		selection.moveCursorToEnd()
	}

	// focus/focusout 이벤트 순서에 의해 active가 제거될 수 있으므로 명시적으로 보장
	const targetBlock = selector.value
	if (targetBlock && targetBlock.dataset.type === 'text') {
		const editorBlockList = [...document.querySelectorAll('.editor--block')]
		editorBlockList.forEach((b: any) => b.classList.remove('active'))
		targetBlock.classList.add('active')
	}
}
